import { EmailAccountType } from "../models/Account";
import { EMAIL_ACCOUNT_CONSTANTS } from "../constants/account-constants";

/**
 * Builds and returns the account fields from the given account data.
 * @param {object} accountData - The source data containing account information.
 * @returns {object} An object representing detailed account fields.
 */
export const buildAccountFields = (accountData): EmailAccountType => {
    // Extract the main account details
    const account = accountData.included.find(item => item.type === 'account');

    // Filter out all account attributes
    const accountAttributes = accountData.included.filter(item => item.type === 'accountAttribute');

    // Retrieve all account field data
    const accountFields = accountData.data;

    // Initialize the object to store the structured account fields
    const fieldsObj: EmailAccountType = {
        id: account.id,
        name: account.attributes.accName,
        username: { value: "", label: "" },
        password: { value: "", label: "" },
        outgoingServer: { value: "", label: "" },
        outgoingPort: { value: '', label: "0" },
        incomingServer: { value: "", label: "" },
        incomingPort: { value: '', label: "0" },
        defaultAccount: account.attributes.accDefault == 1,
        enabled: account.attributes.accActive == 1 ? 1 : 0,
        auth: {value: "", label: "false"},
        ssl: {value: "", label: "false"},
        address: { value: "", label: "" },
        isPasswordCaptured: account.attributes.isPasswordCaptured
    };

    // Process each account field to organize data in fieldsObj
    accountFields.forEach(accountField => {
      const accAttributeId = accountField.relationships.accountAttribute.data.id;
      const accountAttributeLookupName = accountAttributes.find(accAttribute => accAttribute.id === accAttributeId).attributes.aaName;
      const avData = accountField.attributes.avData;
      const accountValueId = accountField.id;

      // Assign account attributes to fieldsObj
      assignAccountAttributes(avData, accountAttributeLookupName, fieldsObj,accountValueId);
    });

    return fieldsObj;
  }

  /**
   * Assigns values to fieldsObj based on the account attribute's name.
   * @param {string} avData - The value to be assigned.
   * @param {string} accountAttributeLookupName - The name of the account attribute.
   * @param {object} fieldsObj - The object where the account data is being organized.
   */
  export const assignAccountAttributes = (avData, accountAttributeLookupName, fieldsObj,accountValueId) => {
    // Switch case to handle different attribute assignments
    switch(accountAttributeLookupName) {
      case EMAIL_ACCOUNT_CONSTANTS.OUTGOING_PORT:
          fieldsObj.outgoingPort = {value:accountValueId, label:avData};
          break;
      case EMAIL_ACCOUNT_CONSTANTS.OUTGOING_SERVER:
          fieldsObj.outgoingServer = {value:accountValueId, label:avData};
          break;
      case EMAIL_ACCOUNT_CONSTANTS.SSL:
          fieldsObj.ssl = {value:accountValueId, label: avData === 'true'};
          break;
      case EMAIL_ACCOUNT_CONSTANTS.AUTH:
          fieldsObj.auth = {value:accountValueId, label: avData === 'true'};
          break;
      case EMAIL_ACCOUNT_CONSTANTS.INCOMING_SERVER:
          fieldsObj.incomingServer = {value:accountValueId, label:avData};
          break;
      case EMAIL_ACCOUNT_CONSTANTS.INCOMING_PORT:
          fieldsObj.incomingPort = {value:accountValueId, label:avData};
          break;
      case EMAIL_ACCOUNT_CONSTANTS.ADDRESS:
          fieldsObj.address = {value:accountValueId, label:avData};
          break;
      case EMAIL_ACCOUNT_CONSTANTS.USERNAME:
          fieldsObj.username = {value:accountValueId, label:avData};
          break;
      case EMAIL_ACCOUNT_CONSTANTS.PASSWORD:
          fieldsObj.password = {value:accountValueId, label:avData};
          break;
    }
  }

interface NestedObjectWithId {
    [key: string]: NestedObjectWithId | string | number | boolean | { id?: string };
}

export function findDifferencesAccountEdit(initial: NestedObjectWithId | EmailAccountType, updated: NestedObjectWithId): NestedObjectWithId {
    const differences: NestedObjectWithId = {};

    for (const key in initial) {
        if (initial.hasOwnProperty(key) && updated.hasOwnProperty(key)) {
            if (JSON.stringify(initial[key]) !== JSON.stringify(updated[key])) {
                differences[key] = updated[key];
            }
        }
    }

    return differences;
}
